import {
    ActionMenu,
    FOCUS_RING,
    formatDate,
    Icon,
    Pills,
    type OptionProps,
} from '@amelio/ui-library';

import type { CareerAspirationHistoryEntry } from '@/types/career-growth';

import { useAmelioTranslations } from '@/hooks';

/**
 * What identifies an entry in the menu: the aspiration it holds, or -- while nothing has been
 * recorded -- the meeting waiting for one. Both are unique per employee.
 */
export const historyEntryKey = (entry: CareerAspirationHistoryEntry): string =>
    entry.aspirationId ?? `meeting-${entry.thematicMeetingId}`;

/** A recorded entry is dated by its answers; a pending one by the meeting it waits for. */
const historyEntryDate = (entry: CareerAspirationHistoryEntry): string =>
    (entry.isPending && entry.meetingScheduledAt) || entry.createdAt;

interface AspirationHistoryMenuProps {
    /** Every "Aspirations de carrière" record of the employee, most recent first. */
    entries: CareerAspirationHistoryEntry[];
    selectedKey: string | null;
    onSelect: (entry: CareerAspirationHistoryEntry) => void;
    dataTestId?: string;
}

/**
 * The trigger is a native button wearing the primary look, rounder than the library's.
 */
const TRIGGER_CLASS = `${FOCUS_RING} bg-primary inline-flex min-h-9 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-white transition hover:brightness-95`;

/**
 * The meeting picker of the aspiration results: a button carrying the selected entry's date,
 * and the library dropdown listing every entry -- the meetings whose answers are still blank are
 * marked as pending. With a single entry, only its date is shown.
 */
export const AspirationHistoryMenu = ({
    entries,
    selectedKey,
    onSelect,
    dataTestId = 'aspiration-history-menu',
}: AspirationHistoryMenuProps) => {
    const { t, i18n } = useAmelioTranslations();

    const selectedEntry =
        entries.find((entry) => historyEntryKey(entry) === selectedKey) ?? entries[0];

    if (!selectedEntry) {
        return null;
    }

    const dateLabel = (entry: CareerAspirationHistoryEntry) =>
        formatDate({ date: historyEntryDate(entry), language: i18n.language });

    // A single meeting leaves nothing to pick, but its date still says which meeting the answers
    // come from: it stays, as a label rather than a trigger opening a one-line menu.
    if (entries.length === 1) {
        return (
            <Pills
                variant={'purpleDeep'}
                size={'sm'}
                dataTestId={`${dataTestId}-date`}
            >
                {dateLabel(selectedEntry)}
            </Pills>
        );
    }

    const options: OptionProps[] = entries.map((entry) => ({
        id: historyEntryKey(entry),
        label: dateLabel(entry),
        description: entry.isPending
            ? t('performanceProfile.growth.aspiration.pending')
            : undefined,
        selected: entry === selectedEntry,
        onClick: () => onSelect(entry),
    }));

    return (
        <ActionMenu
            options={options}
            menuAriaLabel={t('performanceProfile.growth.aspiration.historyAria')}
            dataTestId={dataTestId}
            renderTrigger={(trigger) => (
                <button
                    {...trigger}
                    className={TRIGGER_CLASS}
                    data-testid={`${dataTestId}-trigger`}
                >
                    {dateLabel(selectedEntry)}
                    <Icon
                        name={'arrow-arrow-down'}
                        size={'sm'}
                        ariaHidden
                    />
                </button>
            )}
        />
    );
};
